import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

// The service's scene names, in the order the dots are drawn.
export const SCENES = ['Hook Scene', 'Core Scene', 'End Card'] as const;

const DOT_SX = { width: 4, height: 4, borderRadius: '50%', flexShrink: 0 };

export function CategoryDot({
  category,
  size = 8,
}: {
  category: string;
  size?: number;
}): ReactNode {
  return (
    <Box
      sx={(theme) => ({
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: theme.palette.elementCategory[category] ?? theme.palette.elementCategory.default,
      })}
    />
  );
}

// Dot carries the category colour; the name stays dark.
export function CategoryLabel({ category }: { category: string }): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: 'center', minWidth: 0 }}>
      <CategoryDot category={category} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {category}
      </Typography>
    </Stack>
  );
}

// Where an element appears: one dot per scene, filled when present. Always grey.
export function SceneDots({ scenes }: { scenes: string[] }): ReactNode {
  return (
    <Tooltip title={scenes.join(' · ')}>
      <Stack
        direction="row"
        data-testid="scene-dots"
        aria-label={scenes.join(', ')}
        sx={{ gap: '2px', alignItems: 'center', flexShrink: 0 }}
      >
        {SCENES.map((scene) => (
          <Box
            key={scene}
            sx={(theme) => ({
              ...DOT_SX,
              bgcolor: scenes.includes(scene)
                ? theme.palette.text.muted
                : alpha(theme.palette.text.muted, 0.3),
            })}
          />
        ))}
      </Stack>
    </Tooltip>
  );
}

// Neutral in every workflow, never category-coloured.
export function ElementTag({
  label,
  title,
  scenes = [],
}: {
  label: string;
  // The full name when the label alone is ambiguous ("Included").
  title?: string;
  scenes?: string[];
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        gap: '5px',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        pl: 1,
        pr: '7px',
        py: 0.5,
        borderRadius: '7px',
        bgcolor: 'background.platform',
      }}
    >
      <Tooltip title={title ?? label}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: 'text.muted',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      {scenes.length === 0 ? null : <SceneDots scenes={scenes} />}
    </Stack>
  );
}

export function SceneLegend(): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
      <SceneDots scenes={[...SCENES]} />
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{SCENES.join(' · ')}</Typography>
    </Stack>
  );
}
